import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, share, shareReplay, throwError } from 'rxjs';
import { Entity } from '../models/entity.model';
import { EntityApps } from '../models/entity-apps.model';
import { Pagination, PaginationRequest } from '../models/pagination.model';
import { environment } from 'src/environments/environment';
import * as utils from '../../shared/helpers/utils';

@Injectable({
  providedIn: 'root'
})
export class EntityService {

  getOnlyParents$?: Observable<Pagination<Entity>>;
  getCountries$?: Observable<string>;
  getTimezones$?: Observable<string>;
  private appsCache = new Map<number, { obs: Observable<EntityApps>; at: number }>();
  private readonly APPS_TTL_MS = 10_000; // cubre guard + componente de una misma navegación

  private entitiesSubject = new BehaviorSubject<Pagination<Entity> | null>(null);

  constructor(
    private httpClient: HttpClient
  ) { }

  index(pagination: PaginationRequest): Observable<Pagination<Entity>> {
    console.log(pagination);
    return this.httpClient.get<Pagination<Entity>>(environment.apiUrl + '/users/entities', { params: utils.cleanParams(pagination) });
  }

  insert(entity: Entity): Observable<Entity> {
    return this.httpClient.post<Entity>(environment.apiUrl + '/entities', utils.cleanParams(entity));
  }

  update(entity: Entity, emptyParams?: string[]): Observable<Entity> {
    return this.httpClient.patch<Entity>(environment.apiUrl + '/entities/' + entity.id, utils.cleanParams(entity,emptyParams));
  }

  destroy(id: number): Observable<Entity> {
    return this.httpClient.delete<Entity>(environment.apiUrl + '/entities/' + id);
  }

  getUserEntity(): Observable<Entity> {
    return this.httpClient.get<Entity>(environment.apiUrl + '/users/entities');
  }

  getEntityApps(entityId: number): Observable<EntityApps> {
    // Solo se cachea la entidad del usuario logueado
    if (entityId !== utils.localStorageGetItem('user')?.entityId) {
      return this.httpClient.get<EntityApps>(`${environment.apiUrl}/entities/${entityId}/apps`);
    }

    const hit = this.appsCache.get(entityId);
    if (hit && Date.now() - hit.at < this.APPS_TTL_MS) return hit.obs;

    const obs = this.httpClient.get<EntityApps>(`${environment.apiUrl}/entities/${entityId}/apps`).pipe(
      catchError(err => {
        this.appsCache.delete(entityId); // no cachear el error: la próxima reintenta
        return throwError(() => err);
      }),
      shareReplay(1)
    );
    this.appsCache.set(entityId, { obs, at: Date.now() });
    return obs;
  }

  clearEntityAppsCache(): void {
    this.appsCache.clear();
  }

  getAncestor(entity: Entity): Observable<Entity> {
      return this.httpClient.get<Entity>(environment.apiUrl + '/entities/'+entity.id+'/parents').pipe(share())
  }

  getOnlyParents(): Observable<Pagination<Entity>> {
    if (!this.getOnlyParents$) {
      this.getOnlyParents$ = this.httpClient.get<Pagination<Entity>>(environment.apiUrl + '/users/entities?perPage=-1&onlyParents=true').pipe(share());
    }
    return this.getOnlyParents$;
  }

  getCountries(): Observable<string> {
    if (!this.getCountries$) {
      this.getCountries$ = this.httpClient.get('assets/data/countries.csv', { responseType: 'text' }).pipe(share());
    }
    return this.getCountries$;
  }

  getTimezones(): Observable<string> {
    if (!this.getTimezones$) {
      this.getTimezones$ = this.httpClient.get('assets/data/timezones.csv', { responseType: 'text' }).pipe(share());
    }
    return this.getTimezones$;
  }

}
